import { useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { useUser } from '@taki/auth';
import {
  Button,
  cn,
  IconCog,
  IconModule,
  IconUser,
  NavItem,
  UserChip,
  repliDepuisConfig,
  useBrandingPort,
} from '@taki/ui';
import { TakiWordmark } from './TakiWordmark.js';
import { HOME_PANEL_SCALE, LogoAccueil } from './logoAccueil.js';
import { AppsScreen } from './screens/AppsScreen.js';
import { AccountScreen } from './screens/AccountScreen.js';
import { useScrollShadows } from './useScrollShadows.js';
import type { ShellAppEntry } from './types.js';
// [MENU DEV] Bloc temporaire, suppression : packages/shell/src/dev/README.md
import { useListedAppEntries } from './appListing.js';

/**
 * Sections du panneau Home (rail de gauche).
 *
 * DEUX SECTIONS, ET UNE NAVIGATION. Le rail montre trois entrees (Apps, Mon
 * compte, Parametres), mais « Parametres » n'est PAS une section : c'est un
 * depart vers le hub existant `/settings`, qui a son propre gabarit et ses
 * propres onglets. Le panneau ne le sait pas, il appelle `onOpenSettings` ; le
 * shell navigue et ferme l'overlay, exactement comme pour le choix d'une app.
 */
export type HomeSection = 'apps' | 'account';

/** Libelle editorial de chaque section (overline du contenu). */
const SECTION_LABELS: Record<HomeSection, string> = {
  apps: 'Apps',
  account: 'Mon compte',
};

export interface HomePanelProps {
  /** Config runtime du client (branding, orgId...). */
  config: TakiConfig;
  /** Apps installees, dans l'ordre de la liste de l'org (`settings/apps`). */
  appEntries: readonly ShellAppEntry[];
  /** Selection d'une app : le shell navigue (et ferme l'overlay le cas echeant). */
  onPickApp: (appId: string) => void;
  /** Ouvre le panneau Lia de Taki. */
  onAskAI: () => void;
  /**
   * Ouvre le hub Parametres (`/settings`, onglet Dock par defaut). Le panneau
   * ne connait pas le routeur : le shell lui passe le geste, et c'est lui qui
   * ferme l'overlay quand le panneau est pose au-dessus d'une app.
   */
  onOpenSettings?: () => void;
  /** Section affichee au montage (defaut "apps"). */
  initialSection?: HomeSection;
  /** Classes additionnelles sur la boite du panneau (ex. "relative z-10" en overlay). */
  className?: string;
}

/** Overline de section (ex: "APPS"), repere editorial discret. */
function Overline({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-300">
      {children}
    </span>
  );
}

/**
 * Panneau Home COMPLET (fenetre flottante de la maquette) : rail gauche (logo
 * du branding, nav Apps / Mon compte / Parametres, bouton Lia de Taki) +
 * contenu de la section courante (UserChip, ombres de scroll, mention
 * "powered by").
 *
 * La section est un ETAT INTERNE : changer de section ne navigue jamais. Le
 * meme panneau sert a DEUX endroits :
 *   (a) la route "/" (et /account, qui preselectionne la section via
 *       `initialSection`), pose au centre du fond bg par le shell ;
 *   (b) l'overlay au-dessus d'une app (voile + flou fournis par le shell) :
 *       changer de section ne quitte PAS l'overlay, choisir une app =
 *       `onPickApp` (le shell navigue et ferme).
 *
 * SEUL « Parametres » SORT DU PANNEAU, dans les deux montages et par le meme
 * geste : le hub `/settings` est une surface de travail, avec son dock et son
 * bouton retour ; le redessiner ici en aurait fait une seconde version a tenir.
 */
export function HomePanel({
  config,
  appEntries,
  onPickApp,
  onAskAI,
  onOpenSettings,
  initialSection = 'apps',
  className,
}: HomePanelProps): JSX.Element {
  const { user, claims } = useUser();
  const [section, setSection] = useState<HomeSection>(initialSection);
  const scrollShadows = useScrollShadows();

  // Un changement de route (/ -> /account) pendant que le panneau reste monte
  // doit re-preselectionner la section ; l'etat interne garde la main ensuite.
  useEffect(() => {
    setSection(initialSection);
  }, [initialSection]);

  const userName = user?.displayName ?? user?.email ?? 'Utilisateur';

  // [MENU DEV] Le masquage s'applique a la liste AFFICHEE ; la prop appEntries
  // reste la liste COMPLETE (l'ecran DEV, qui seul recoche une app masquee,
  // vit desormais dans le hub Parametres et la recoit de la, pas d'ici).
  const visibleApps = useListedAppEntries(appEntries);

  /* Le nom et le logo du client viennent du PORT (le choix fait dans
     Parametres > Environnement, fusionne avec la config) ; sans port, c'est la
     config de deploiement, comme avant ce chantier. */
  const courant = useBrandingPort()?.courant ?? repliDepuisConfig(config.branding);

  let content: ReactNode;
  if (section === 'account') {
    content = user != null ? <AccountScreen config={config} user={user} claims={claims} /> : null;
  } else {
    // [MENU DEV] remettre `appEntries` a la place de `visibleApps` a la suppression.
    // `installedApps` reste la liste COMPLETE, et le restera : c'est elle qui
    // permet a l'etat vide de distinguer « rien d'installe » de « rien
    // d'accessible ».
    content = (
      <AppsScreen apps={visibleApps} installedApps={appEntries} onPickApp={onPickApp} />
    );
  }

  return (
    <div
      className={cn(
        'flex h-[min(86vh,45rem)] w-full max-w-[75rem] overflow-hidden rounded-[1.625rem] border border-border bg-surface shadow-panel',
        className,
      )}
      style={{ zoom: HOME_PANEL_SCALE }}
    >
      {/* RAIL gauche */}
      <div className="flex w-[19.5rem] shrink-0 flex-col border-r border-border-soft p-[2.375rem_1.625rem_1.875rem]">
        <div className="mb-[2.375rem] px-2.5">
          {/* Sa taille vit dans `logoAccueil.tsx` : l'apercu de Parametres
              monte le meme composant. */}
          <LogoAccueil nom={courant.name} logo={courant.logo} />
        </div>

        <nav className="flex flex-col gap-2">
          <NavItem
            icon={<IconModule size={20} />}
            label="Apps"
            active={section === 'apps'}
            onClick={() => setSection('apps')}
          />
          <NavItem
            icon={<IconUser size={20} />}
            label="Mon compte"
            active={section === 'account'}
            onClick={() => setSection('account')}
          />
          {/* PARAMETRES N'EST PAS UNE SECTION : jamais `active`, il PART vers
              le hub `/settings`. Sans geste fourni par l'hote (panneau monte
              hors routeur, tests), l'entree reste inerte plutot que de
              promettre une navigation qu'elle ne peut pas faire. */}
          <NavItem
            icon={<IconCog size={20} />}
            label="Parametres"
            active={false}
            onClick={() => onOpenSettings?.()}
          />
        </nav>

        <div className="flex-1" />

        <Button variant="ai" size="lg" className="h-[3.375rem] w-full" onClick={onAskAI}>
          Ask Lia
        </Button>
      </div>

      {/* CONTENU droite. Le padding bas (5.5rem) reserve la bande blanche qui
          porte le "powered by" : il faut qu'elle respire autour du lockup,
          sinon le logo colle au bord du panneau. */}
      <div className="relative flex min-w-0 flex-1 flex-col p-[2.375rem_2.5rem_5.5rem]">
        <div className="mb-[1.625rem] flex items-center justify-between">
          <Overline>{SECTION_LABELS[section]}</Overline>
          <UserChip
            name={userName}
            avatarUrl={user?.photoURL ?? undefined}
            onClick={() => setSection('account')}
          />
        </div>

        {/* Zone qui defile + son voile : le masque EFFACE le contenu au bord
            du cadre (rien n'est peint par-dessus), et chaque bord ne fond que
            s'il reste a faire defiler de ce cote. */}
        <div
          ref={scrollShadows.scrollRef}
          className={cn(
            'taki-scroll-mask min-h-0 flex-1 overflow-y-auto',
            scrollShadows.hasAbove && 'taki-scroll-mask-top',
            scrollShadows.hasBelow && 'taki-scroll-mask-bottom',
          )}
        >
          <div ref={scrollShadows.contentRef}>
            {/* Fondu discret rejoue a chaque changement de section (la cle
                remonte le contenu ; l'enveloppe mesuree, elle, ne bouge pas). */}
            <div key={section} className="taki-anim-fade">
              {content}
            </div>
          </div>
        </div>

        {/* Centre verticalement dans la bande de 5.5rem : 1.875rem dessous,
            autant au-dessus jusqu'a la zone de contenu. */}
        <div className="pointer-events-none absolute bottom-[1.875rem] right-8 flex items-center gap-2 opacity-50">
          <span className="text-[0.625rem] tracking-[0.04em] text-ink-500">powered by</span>
          {/* 29 : valeur de la maquette (Home, mention "powered by"). */}
          <TakiWordmark height={29} className="text-ink-500" />
        </div>
      </div>
    </div>
  );
}
